import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import "../../components/EventUserDashboard.css";

interface BarStatusDto {
    eventBarId: number;
    barName: string;
    maxDrinks?: number;
    usedDrinks?: number;
    remainingDrinks: number;
    validDate?: string | null;
}

interface PersonDto {
    personId: number;
    firstName?: string;
    lastName?: string;
    fullName?: string;
}

interface TicketStatusDto {
    ticketId?: number;
    ticketCode: string;
    collectionName?: string;
    ticketNumber?: number;

    eventBarId?: number;
    bar?: number;
    barName?: string;

    maxDrinks?: number;
    usedDrinks?: number;
    remainingDrinks: number;
    validDate?: string | null;

    message?: string;
    isActive?: boolean;
}

enum DrinkType {
    BEER = 1,
    WATER = 2,
    WINE = 3,
}

const toArray = <T,>(result: any): T[] => {
    if (Array.isArray(result)) return result;
    if (Array.isArray(result?.data)) return result.data;
    if (Array.isArray(result?.items)) return result.items;
    if (Array.isArray(result?.persons)) return result.persons;
    if (Array.isArray(result?.bars)) return result.bars;
    if (Array.isArray(result?.results)) return result.results;

    return [];
};

const toNumber = (
    value: any,
    fallback = 0
): number => {
    const num = Number(value);

    return Number.isFinite(num)
        ? num
        : fallback;
};

const isNumericId = (value: string) =>
    /^\d+$/.test(value);

const formatDate = (
    value?: string | null
) => {
    if (!value) return "";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleDateString("en-GB");
};

const getTodayString = () => {
    const today = new Date();

    const yyyy = today.getFullYear();
    const mm = String(
        today.getMonth() + 1
    ).padStart(2, "0");

    const dd = String(
        today.getDate()
    ).padStart(2, "0");

    return `${yyyy}-${mm}-${dd}`;
};

const readErrorMessage = async (
    res: Response,
    fallback: string
) => {
    try {
        const contentType =
            res.headers.get("content-type") || "";

        if (
            contentType.includes(
                "application/json"
            )
        ) {
            const json = await res.json();

            if (
                json?.message ===
                "Ticket is not valid today" &&
                json?.validDate
            ) {
                return `Ticket is only valid ${formatDate(
                    json.validDate
                )}.`;
            }

            if (
                json?.message ===
                "Ticket fully used"
            ) {
                return "This ticket has been fully used.";
            }

            return (
                json?.message ||
                json?.error ||
                json?.title ||
                json?.detail ||
                JSON.stringify(json)
            );
        }

        const text = await res.text();

        if (text === "Ticket fully used") {
            return "This ticket has been fully used.";
        }

        return text || fallback;
    } catch {
        return fallback;
    }
};

const FormDrinkOrder: React.FC = () => {
    const apiUrl =
        process.env.REACT_APP_API_URL || "";

    const params = useParams();
    const location = useLocation();

    const parsedEventId = toNumber(
        params.eventId,
        0
    );

    const rawId = decodeURIComponent(
        params.id || ""
    );

    // Numeric ID = normal person allowance.
    // Non-numeric ID = DrinkTicket QR code.
    const isPersonFlow =
        isNumericId(rawId);

    const parsedPersonId = isPersonFlow
        ? toNumber(rawId, 0)
        : 0;

    const ticketCode = isPersonFlow
        ? ""
        : rawId;

    const [bars, setBars] = useState<
        BarStatusDto[]
    >([]);

    const [
        selectedBar,
        setSelectedBar,
    ] = useState<number | null>(null);

    const [
        displayName,
        setDisplayName,
    ] = useState("");

    const [beerCount, setBeerCount] =
        useState(0);

    const [waterCount, setWaterCount] =
        useState(0);

    const [wineCount, setWineCount] =
        useState(0);

    const [success, setSuccess] =
        useState<string | null>(null);

    const [
        deliveryText,
        setDeliveryText,
    ] = useState<string | null>(null);

    const [error, setError] =
        useState<string | null>(null);

    const [loading, setLoading] =
        useState(false);

    const [
        isSubmitting,
        setIsSubmitting,
    ] = useState(false);

    const [
        showConfirmScreen,
        setShowConfirmScreen,
    ] = useState(false);

    const [
        ticketValidDate,
        setTicketValidDate,
    ] = useState<string | null>(null);

    const [
        personValidDate,
        setPersonValidDate,
    ] = useState<string>(
        getTodayString()
    );

    // ---------------------------------------------------------
    // QUERY PARAMETERS
    // ---------------------------------------------------------

    const barFromQuery = useMemo(() => {
        try {
            const queryParams = new URLSearchParams(
                location.search || ""
            );

            return toNumber(
                queryParams.get("eventBarId") ??
                queryParams.get("bar"),
                0
            );
        } catch {
            return 0;
        }
    }, [location.search]);

    const barNameFromQuery =
        useMemo(() => {
            try {
                const queryParams =
                    new URLSearchParams(
                        location.search || ""
                    );

                return (
                    queryParams.get(
                        "barName"
                    ) || ""
                );
            } catch {
                return "";
            }
        }, [location.search]);

    // ---------------------------------------------------------
    // ORDER VALUES
    // ---------------------------------------------------------

    const totalInCart =
        beerCount +
        waterCount +
        wineCount;

    const selectedBarStatus =
        useMemo(() => {
            return (
                bars.find(
                    (b) =>
                        b.eventBarId ===
                        selectedBar
                ) ?? null
            );
        }, [bars, selectedBar]);

    const selectedBarName =
        selectedBarStatus?.barName ||
        barNameFromQuery ||
        (selectedBar
            ? `Bar ${selectedBar}`
            : "");

    const remainingForSelectedBar =
        toNumber(
            selectedBarStatus
                ?.remainingDrinks,
            0
        );

    const canAddMore =
        totalInCart <
        remainingForSelectedBar;

    const resetOrderCounts = () => {
        setBeerCount(0);
        setWaterCount(0);
        setWineCount(0);
    };

    // ---------------------------------------------------------
    // PERSON
    // ---------------------------------------------------------

    const fetchPerson = async () => {
        try {
            const res = await fetch(
                `${apiUrl}/api/events/${parsedEventId}/persons`
            );

            if (!res.ok) {
                setDisplayName(
                    `Person ${parsedPersonId}`
                );

                return;
            }

            const result =
                await res.json();

            const persons =
                toArray<PersonDto>(
                    result
                );

            const person =
                persons.find(
                    (p) =>
                        toNumber(
                            p?.personId
                        ) ===
                        parsedPersonId
                );

            if (!person) {
                setDisplayName(
                    `Person ${parsedPersonId}`
                );

                return;
            }

            const name =
                person.fullName ||
                `${person.firstName || ""
                    } ${person.lastName || ""
                    }`.trim();

            setDisplayName(
                name ||
                `Person ${parsedPersonId}`
            );
        } catch (err) {
            console.error(
                "Error in fetchPerson:",
                err
            );

            setDisplayName(
                `Person ${parsedPersonId}`
            );
        }
    };

    // ---------------------------------------------------------
    // PERSON DRINK ALLOWANCE STATUS
    // ---------------------------------------------------------

    const fetchPersonStatus =
        async () => {
            try {
                const res = await fetch(
                    `${apiUrl}/api/drinks/status` +
                    `?personId=${parsedPersonId}` +
                    `&eventId=${parsedEventId}` +
                    `&validDate=${encodeURIComponent(
                        personValidDate
                    )}`
                );

                if (!res.ok) {
                    const text =
                        await readErrorMessage(
                            res,
                            "Could not fetch drink status"
                        );

                    console.error(
                        "Error in fetchPersonStatus:",
                        text
                    );

                    setError(text);
                    setBars([]);
                    setSelectedBar(null);

                    return;
                }

                const result =
                    await res.json();

                const rawData =
                    toArray<any>(
                        result
                    );

                /*
                 * Normalize API response.
                 *
                 * New API:
                 * {
                 *   eventBarId: 7,
                 *   barName: "Crew Bar",
                 *   remainingDrinks: 10
                 * }
                 *
                 * The fallback to x.bar is only
                 * here temporarily if an older
                 * backend response is encountered.
                 */
                const data: BarStatusDto[] =
                    rawData.map(
                        (x) => ({
                            eventBarId:
                                toNumber(
                                    x.eventBarId ??
                                    x.bar,
                                    0
                                ),

                            barName:
                                x.barName ||
                                (toNumber(
                                    x.eventBarId ??
                                    x.bar,
                                    0
                                ) ===
                                    barFromQuery &&
                                    barNameFromQuery
                                    ? barNameFromQuery
                                    : ""),

                            maxDrinks:
                                x.maxDrinks,

                            usedDrinks:
                                x.usedDrinks,

                            remainingDrinks:
                                toNumber(
                                    x.remainingDrinks,
                                    0
                                ),

                            validDate:
                                x.validDate,
                        })
                    );

                if (
                    data.length > 0 &&
                    data[0]?.validDate
                ) {
                    setPersonValidDate(
                        data[0].validDate
                    );
                }

                const activeBars =
                    data.filter(
                        (b) =>
                            b.eventBarId >
                            0 &&
                            b.remainingDrinks >
                            0
                    );

                setBars(activeBars);
            } catch (err) {
                console.error(
                    "Error in fetchPersonStatus:",
                    err
                );

                setError(
                    "Could not fetch drink status"
                );

                setBars([]);
                setSelectedBar(null);
            }
        };

    // ---------------------------------------------------------
    // DRINK TICKET / COLLECTION STATUS
    // ---------------------------------------------------------

    const fetchTicketStatus =
        async () => {
            try {
                setTicketValidDate(
                    null
                );

                const res = await fetch(
                    `${apiUrl}/api/drink-tickets/status` +
                    `?ticketCode=${encodeURIComponent(
                        ticketCode
                    )}` +
                    `&eventId=${parsedEventId}`
                );

                if (!res.ok) {
                    const msg =
                        await readErrorMessage(
                            res,
                            "Could not fetch ticket status"
                        );

                    console.error(
                        "Error in fetchTicketStatus:",
                        msg
                    );

                    setError(msg);
                    setBars([]);
                    setSelectedBar(null);
                    setDisplayName(
                        ticketCode
                    );

                    return;
                }

                const result: TicketStatusDto =
                    await res.json();

                setTicketValidDate(
                    result.validDate ||
                    null
                );

                setDisplayName(
                    result.collectionName
                        ? `${result.collectionName
                        }${result.ticketNumber
                            ? ` #${result.ticketNumber}`
                            : ""
                        }`
                        : result.ticketCode
                );

                const eventBarId =
                    toNumber(
                        result.eventBarId ??
                        result.bar,
                        0
                    );

                const barName =
                    result.barName ||
                    (eventBarId ===
                        barFromQuery &&
                        barNameFromQuery
                        ? barNameFromQuery
                        : "");

                const activeBars: BarStatusDto[] =
                    toNumber(
                        result.remainingDrinks,
                        0
                    ) > 0 &&
                        eventBarId > 0
                        ? [
                            {
                                eventBarId,
                                barName,

                                maxDrinks:
                                    result.maxDrinks,

                                usedDrinks:
                                    result.usedDrinks,

                                remainingDrinks:
                                    result.remainingDrinks,

                                validDate:
                                    result.validDate,
                            },
                        ]
                        : [];

                setBars(activeBars);

                if (
                    activeBars.length ===
                    0 &&
                    !result.message
                ) {
                    setError(
                        "This ticket has no drinks remaining."
                    );
                }
            } catch (err) {
                console.error(
                    "Error in fetchTicketStatus:",
                    err
                );

                setError(
                    "Could not fetch ticket status"
                );

                setBars([]);
                setSelectedBar(null);
            }
        };

    // ---------------------------------------------------------
    // INITIAL LOAD
    // ---------------------------------------------------------

    useEffect(() => {
        const load = async () => {
            if (
                !parsedEventId ||
                !rawId
            ) {
                setError(
                    "Invalid eventId or id."
                );

                setBars([]);
                setSelectedBar(null);

                return;
            }

            try {
                setLoading(true);
                setError(null);
                setSuccess(null);
                setDeliveryText(null);

                if (isPersonFlow) {
                    await Promise.all([
                        fetchPerson(),
                        fetchPersonStatus(),
                    ]);
                } else {
                    await fetchTicketStatus();
                }
            } finally {
                setLoading(false);
            }
        };

        load();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        parsedEventId,
        rawId,
        isPersonFlow,
        personValidDate,
    ]);

    // ---------------------------------------------------------
    // SELECT BAR
    // ---------------------------------------------------------

    useEffect(() => {
        if (bars.length === 0) {
            setSelectedBar(null);
            return;
        }

        // Prefer the bar selected from
        // EventPersonOversigt.
        if (barFromQuery > 0) {
            const exists =
                bars.some(
                    (b) =>
                        b.eventBarId ===
                        barFromQuery
                );

            if (exists) {
                setSelectedBar(
                    barFromQuery
                );

                return;
            }
        }

        if (
            selectedBar === null ||
            !bars.some(
                (b) =>
                    b.eventBarId ===
                    selectedBar
            )
        ) {
            setSelectedBar(
                bars[0].eventBarId
            );
        }
    }, [
        bars,
        barFromQuery,
        selectedBar,
    ]);

    // ---------------------------------------------------------
    // ADD / REMOVE DRINKS
    // ---------------------------------------------------------

    const addDrink = (
        type: DrinkType
    ) => {
        if (
            !selectedBarStatus ||
            !canAddMore
        ) {
            return;
        }

        setSuccess(null);
        setDeliveryText(null);
        setError(null);

        if (
            type === DrinkType.BEER
        ) {
            setBeerCount(
                (b) => b + 1
            );
        }

        if (
            type === DrinkType.WATER
        ) {
            setWaterCount(
                (w) => w + 1
            );
        }

        if (
            type === DrinkType.WINE
        ) {
            setWineCount(
                (w) => w + 1
            );
        }
    };

    const removeDrink = (
        type: DrinkType
    ) => {
        setSuccess(null);
        setDeliveryText(null);
        setError(null);

        if (
            type === DrinkType.BEER
        ) {
            setBeerCount((b) =>
                Math.max(
                    0,
                    b - 1
                )
            );
        }

        if (
            type === DrinkType.WATER
        ) {
            setWaterCount((w) =>
                Math.max(
                    0,
                    w - 1
                )
            );
        }

        if (
            type === DrinkType.WINE
        ) {
            setWineCount((w) =>
                Math.max(
                    0,
                    w - 1
                )
            );
        }
    };

    // ---------------------------------------------------------
    // OPEN CONFIRMATION
    // ---------------------------------------------------------

    const openConfirmScreen =
        () => {
            if (
                !selectedBar ||
                !selectedBarStatus ||
                totalInCart <= 0
            ) {
                return;
            }

            const remainingDrinks =
                toNumber(
                    selectedBarStatus.remainingDrinks,
                    0
                );

            if (
                totalInCart >
                remainingDrinks
            ) {
                setError(
                    "You cannot order more drinks than you have remaining."
                );

                return;
            }

            setSuccess(null);
            setDeliveryText(null);
            setError(null);

            setShowConfirmScreen(
                true
            );
        };

    // ---------------------------------------------------------
    // REFRESH
    // ---------------------------------------------------------

    const refreshStatus =
        async () => {
            if (isPersonFlow) {
                await fetchPersonStatus();
            } else {
                await fetchTicketStatus();
            }
        };

    // ---------------------------------------------------------
    // BARTENDER PRESSES "DELIVER"
    // ---------------------------------------------------------

    const confirmDelivery =
        async () => {
            if (
                !selectedBar ||
                !selectedBarStatus ||
                totalInCart <= 0 ||
                isSubmitting
            ) {
                return;
            }

            const remainingDrinks =
                toNumber(
                    selectedBarStatus.remainingDrinks,
                    0
                );

            if (
                totalInCart >
                remainingDrinks
            ) {
                setError(
                    "You cannot order more drinks than you have remaining."
                );

                setShowConfirmScreen(
                    false
                );

                return;
            }

            setSuccess(null);
            setDeliveryText(null);
            setError(null);
            setIsSubmitting(true);

            try {
                // =============================================
                // NORMAL PERSON / DRINK ALLOWANCE
                //
                // ONE API request for the complete order.
                // DrinkAllowance is only deducted here.
                // =============================================

                if (isPersonFlow) {
                    const res =
                        await fetch(
                            `${apiUrl}/api/drinks/order`,
                            {
                                method: "POST",

                                headers: {
                                    "Content-Type":
                                        "application/json",
                                },

                                body: JSON.stringify(
                                    {
                                        personId:
                                            parsedPersonId,

                                        eventId:
                                            parsedEventId,

                                        eventBarId:
                                            selectedBar,

                                        beer:
                                            beerCount,

                                        water:
                                            waterCount,

                                        wine:
                                            wineCount,

                                        validDate:
                                            personValidDate,
                                    }
                                ),
                            }
                        );

                    if (!res.ok) {
                        const text =
                            await readErrorMessage(
                                res,
                                `Order failed with status ${res.status}`
                            );

                        throw new Error(
                            text
                        );
                    }
                }

                // =============================================
                // COLLECTION / DRINK TICKET
                //
                // Keep the existing ticket endpoint for now.
                // =============================================

                else {
                    const orders: DrinkType[] =
                        [
                            ...Array(
                                beerCount
                            ).fill(
                                DrinkType.BEER
                            ),

                            ...Array(
                                waterCount
                            ).fill(
                                DrinkType.WATER
                            ),

                            ...Array(
                                wineCount
                            ).fill(
                                DrinkType.WINE
                            ),
                        ];

                    for (
                        const drinkType of orders
                    ) {
                        const res =
                            await fetch(
                                `${apiUrl}/api/drink-tickets/order`,
                                {
                                    method:
                                        "POST",

                                    headers: {
                                        "Content-Type":
                                            "application/json",
                                    },

                                    body: JSON.stringify(
                                        {
                                            ticketCode,

                                            eventId:
                                                parsedEventId,

                                            drinkType,
                                        }
                                    ),
                                }
                            );

                        if (!res.ok) {
                            const text =
                                await readErrorMessage(
                                    res,
                                    `Order failed with status ${res.status}`
                                );

                            throw new Error(
                                text
                            );
                        }
                    }
                }

                // =============================================
                // SUCCESS
                // =============================================

                const deliveredItems =
                    [
                        beerCount > 0
                            ? `${beerCount} Beer`
                            : null,

                        waterCount > 0
                            ? `${waterCount} Water`
                            : null,

                        wineCount > 0
                            ? `${wineCount} Wine`
                            : null,
                    ]
                        .filter(Boolean)
                        .join(", ");

                setDeliveryText(
                    `Udleveret: ${new Date().toLocaleString(
                        "da-DK"
                    )} — ${deliveredItems}`
                );

                setSuccess(
                    "Drinks delivered"
                );

                setShowConfirmScreen(
                    false
                );

                resetOrderCounts();

                // Refresh the allowance so the
                // new remaining amount is loaded.
                if (isPersonFlow) {
                    await refreshStatus();
                } else {
                    setBars([]);
                    setSelectedBar(
                        null
                    );
                }
            } catch (err: any) {
                console.error(
                    "Error in confirmDelivery:",
                    err
                );

                setError(
                    err?.message ||
                    "An error occurred during delivery."
                );

                setShowConfirmScreen(
                    false
                );
            } finally {
                setIsSubmitting(
                    false
                );
            }
        };

    // ---------------------------------------------------------
    // RENDER
    // ---------------------------------------------------------

    return (
        <div className="dashContainer">
            <div className="wrapper">
                <div className="top">
                    <div className="name">
                        {isPersonFlow
                            ? `Hello ${displayName ||
                            `Person ${parsedPersonId}`
                            }`
                            : displayName ||
                            ticketCode}
                    </div>

                    <div className="meta">
                        {selectedBarStatus
                            ? `Order at ${selectedBarName}`
                            : "Order your drinks"}
                    </div>
                </div>

                <div className="content">
                    {loading && (
                        <div
                            style={{
                                marginBottom: 16,
                            }}
                        >
                            Loading data...
                        </div>
                    )}

                    {success && (
                        <div
                            className="badgeRed"
                            style={{
                                padding: 8,
                                marginBottom: 16,
                            }}
                        >
                            <div>
                                {success}
                            </div>

                            {deliveryText && (
                                <div
                                    style={{
                                        marginTop: 6,
                                    }}
                                >
                                    {
                                        deliveryText
                                    }
                                </div>
                            )}
                        </div>
                    )}

                    {!success &&
                        error && (
                            <div
                                style={{
                                    padding: 8,
                                    marginBottom: 16,
                                    background:
                                        "#ffdede",
                                    color: "#900",
                                    borderRadius: 8,
                                }}
                            >
                                {error}
                            </div>
                        )}

                    {!success &&
                        isPersonFlow &&
                        personValidDate && (
                            <div
                                style={{
                                    padding: 8,
                                    marginBottom: 16,
                                    background:
                                        "#eef6ff",
                                    color: "#124",
                                    borderRadius: 8,
                                }}
                            >
                                Valid date:{" "}
                                <strong>
                                    {formatDate(
                                        personValidDate
                                    )}
                                </strong>
                            </div>
                        )}

                    {!success &&
                        !isPersonFlow &&
                        ticketValidDate && (
                            <div
                                style={{
                                    padding: 8,
                                    marginBottom: 16,
                                    background:
                                        "#eef6ff",
                                    color: "#124",
                                    borderRadius: 8,
                                }}
                            >
                                Valid date:{" "}
                                <strong>
                                    {formatDate(
                                        ticketValidDate
                                    )}
                                </strong>
                            </div>
                        )}

                    {/* =========================================
                        ORDER SCREEN
                    ========================================= */}

                    {!success &&
                        selectedBarStatus &&
                        !showConfirmScreen && (
                            <>
                                <div className="sectionTitle">
                                    Order at{" "}
                                    {
                                        selectedBarName
                                    }
                                </div>

                                <div
                                    style={{
                                        marginTop: 12,
                                    }}
                                >
                                    Remaining in
                                    bar:{" "}
                                    <strong>
                                        {
                                            remainingForSelectedBar
                                        }
                                    </strong>
                                </div>

                                <div
                                    className="stat"
                                    style={{
                                        padding: 16,
                                        marginTop: 16,
                                        display:
                                            "grid",
                                        gap: 12,
                                    }}
                                >
                                    {/* BEER */}

                                    <div
                                        style={{
                                            display:
                                                "flex",
                                            justifyContent:
                                                "space-between",
                                            alignItems:
                                                "center",
                                        }}
                                    >
                                        <span>
                                            🍺 Beer
                                        </span>

                                        <div
                                            style={{
                                                display:
                                                    "flex",
                                                gap: 8,
                                                alignItems:
                                                    "center",
                                            }}
                                        >
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    removeDrink(
                                                        DrinkType.BEER
                                                    )
                                                }
                                            >
                                                -
                                            </button>

                                            <strong>
                                                {
                                                    beerCount
                                                }
                                            </strong>

                                            <button
                                                type="button"
                                                onClick={() =>
                                                    addDrink(
                                                        DrinkType.BEER
                                                    )
                                                }
                                                disabled={
                                                    !canAddMore
                                                }
                                            >
                                                +
                                            </button>
                                        </div>
                                    </div>

                                    {/* WATER */}

                                    <div
                                        style={{
                                            display:
                                                "flex",
                                            justifyContent:
                                                "space-between",
                                            alignItems:
                                                "center",
                                        }}
                                    >
                                        <span>
                                            🥤 Water
                                        </span>

                                        <div
                                            style={{
                                                display:
                                                    "flex",
                                                gap: 8,
                                                alignItems:
                                                    "center",
                                            }}
                                        >
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    removeDrink(
                                                        DrinkType.WATER
                                                    )
                                                }
                                            >
                                                -
                                            </button>

                                            <strong>
                                                {
                                                    waterCount
                                                }
                                            </strong>

                                            <button
                                                type="button"
                                                onClick={() =>
                                                    addDrink(
                                                        DrinkType.WATER
                                                    )
                                                }
                                                disabled={
                                                    !canAddMore
                                                }
                                            >
                                                +
                                            </button>
                                        </div>
                                    </div>

                                    {/* WINE */}

                                    <div
                                        style={{
                                            display:
                                                "flex",
                                            justifyContent:
                                                "space-between",
                                            alignItems:
                                                "center",
                                        }}
                                    >
                                        <span>
                                            🍷 Wine
                                        </span>

                                        <div
                                            style={{
                                                display:
                                                    "flex",
                                                gap: 8,
                                                alignItems:
                                                    "center",
                                            }}
                                        >
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    removeDrink(
                                                        DrinkType.WINE
                                                    )
                                                }
                                            >
                                                -
                                            </button>

                                            <strong>
                                                {
                                                    wineCount
                                                }
                                            </strong>

                                            <button
                                                type="button"
                                                onClick={() =>
                                                    addDrink(
                                                        DrinkType.WINE
                                                    )
                                                }
                                                disabled={
                                                    !canAddMore
                                                }
                                            >
                                                +
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* ORDER SUMMARY */}

                                <div
                                    className="stat"
                                    style={{
                                        padding: 16,
                                        marginTop: 20,
                                    }}
                                >
                                    <div>
                                        🍺{" "}
                                        {
                                            beerCount
                                        }
                                    </div>

                                    <div>
                                        🥤{" "}
                                        {
                                            waterCount
                                        }
                                    </div>

                                    <div>
                                        🍷{" "}
                                        {
                                            wineCount
                                        }
                                    </div>

                                    <div
                                        style={{
                                            marginTop: 10,
                                            fontWeight: 800,
                                        }}
                                    >
                                        Total:{" "}
                                        {
                                            totalInCart
                                        }
                                    </div>

                                    <div
                                        style={{
                                            marginTop: 8,
                                        }}
                                    >
                                        Remaining
                                        after
                                        selection:{" "}
                                        {Math.max(
                                            0,
                                            remainingForSelectedBar -
                                            totalInCart
                                        )}
                                    </div>

                                    <button
                                        type="button"
                                        style={{
                                            marginTop: 15,
                                            width: "100%",
                                            opacity:
                                                totalInCart <=
                                                    0
                                                    ? 0.6
                                                    : 1,
                                            cursor:
                                                totalInCart <=
                                                    0
                                                    ? "not-allowed"
                                                    : "pointer",
                                        }}
                                        onClick={
                                            openConfirmScreen
                                        }
                                        disabled={
                                            totalInCart <=
                                            0
                                        }
                                    >
                                        Order
                                    </button>
                                </div>
                            </>
                        )}

                    {/* =========================================
                        BARTENDER CONFIRMATION
                    ========================================= */}

                    {!success &&
                        selectedBarStatus &&
                        showConfirmScreen && (
                            <div
                                className="stat"
                                style={{
                                    padding: 30,
                                    marginTop: 20,
                                    minHeight: 420,
                                    display:
                                        "flex",
                                    flexDirection:
                                        "column",
                                    justifyContent:
                                        "center",
                                    alignItems:
                                        "center",
                                    textAlign:
                                        "center",
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: 34,
                                        fontWeight: 800,
                                    }}
                                >
                                    {
                                        selectedBarName
                                    }
                                </div>

                                {!isPersonFlow && (
                                    <div
                                        style={{
                                            fontSize: 20,
                                            marginTop: 12,
                                        }}
                                    >
                                        {
                                            displayName
                                        }
                                    </div>
                                )}

                                <div
                                    style={{
                                        fontSize: 24,
                                        marginTop: 20,
                                    }}
                                >
                                    Deliver the
                                    following:
                                </div>

                                <div
                                    style={{
                                        fontSize: 40,
                                        fontWeight: 800,
                                        marginTop: 30,
                                        lineHeight: 1.6,
                                    }}
                                >
                                    {beerCount >
                                        0 && (
                                            <div>
                                                🍺{" "}
                                                {
                                                    beerCount
                                                }{" "}
                                                Beer
                                            </div>
                                        )}

                                    {waterCount >
                                        0 && (
                                            <div>
                                                🥤{" "}
                                                {
                                                    waterCount
                                                }{" "}
                                                Water
                                            </div>
                                        )}

                                    {wineCount >
                                        0 && (
                                            <div>
                                                🍷{" "}
                                                {
                                                    wineCount
                                                }{" "}
                                                Wine
                                            </div>
                                        )}
                                </div>

                                <div
                                    style={{
                                        fontSize: 28,
                                        fontWeight: 800,
                                        marginTop: 25,
                                    }}
                                >
                                    Total:{" "}
                                    {
                                        totalInCart
                                    }
                                </div>

                                <div
                                    style={{
                                        display:
                                            "flex",
                                        gap: 12,
                                        width: "100%",
                                        marginTop: 30,
                                    }}
                                >
                                    <button
                                        type="button"
                                        style={{
                                            flex: 1,
                                            padding: 16,
                                        }}
                                        onClick={() =>
                                            setShowConfirmScreen(
                                                false
                                            )
                                        }
                                        disabled={
                                            isSubmitting
                                        }
                                    >
                                        Back
                                    </button>

                                    <button
                                        type="button"
                                        style={{
                                            flex: 1,
                                            padding: 16,
                                            fontWeight: 800,
                                        }}
                                        onClick={
                                            confirmDelivery
                                        }
                                        disabled={
                                            isSubmitting
                                        }
                                    >
                                        {isSubmitting
                                            ? "Saving..."
                                            : "Udleveret"}
                                    </button>
                                </div>
                            </div>
                        )}

                    {!success &&
                        !loading &&
                        !selectedBarStatus &&
                        !error && (
                            <div
                                style={{
                                    marginTop: 20,
                                }}
                            >
                                You have no
                                drinks remaining.
                            </div>
                        )}
                </div>
            </div>
        </div>
    );
};

export default FormDrinkOrder;